/* eslint-disable max-lines */
/* eslint-disable no-restricted-imports */
// FSD VIOLATION: Tab state management needs artwork and composition form data.
// Core state hook used across all layers - cannot be moved without breaking architecture.
import { useAtom, useSetAtom } from "jotai";
import { CloudUpload, PaintBucket, Palette, Ruler } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import {
  useArtworkForm,
  usePlacementPosition,
  mobileSizePlacementOpenAtom
} from "@design-lab/entities/artwork";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { getPlacementFromSide } from "@design-lab/shared/lib/artworkUtils";
import { trackPlacementSyncDivergence, trackTabNavigation } from "@design-lab/shared/lib/analytics";
import type { Tab, DesignSubTab, AppTab } from "@design-lab/shared/types/tab";
import {
  designSubTabAtom,
  designSubTabInitializedAtom,
  tabInfoExpandedAtom
} from "@design-lab/shared/lib/tab-navigation";
import DesignIcon from "assets/icons/design.svg";
import DesignSelectedIcon from "assets/icons/design-selected.svg";
import SizesIcon from "assets/icons/sizes.svg";
import SizesSelectedIcon from "assets/icons/sizes-selected.svg";
import ProductsIcon from "assets/icons/products.svg";
import ProductsSelectedIcon from "assets/icons/products-selected.svg";

// MN-9145 T9: useTab (and thus the safety-net effect below) is mounted by ~10
// consumers at once (DesignLabPage, TabCard, SideSwitcher, NextButton, ...). A
// single unrouted designSubTabAtom write is observed by EVERY mounted instance,
// so an ungated emit would fire the divergence breadcrumb + dev warn once PER
// INSTANCE — inflating the "expected rate zero" evidence metric ~Nx. Collapse
// concurrent instances to exactly one emit per transition by keying on the
// divergence signature; every instance reads the same shared atoms/form so they
// compute an identical key and only the first emits. The reconciling
// setPlacement WRITE stays per-instance (idempotent) — only the EMIT dedupes.
let lastEmittedDivergenceKey: string | null = null;

// Test-only: clear the cross-instance emit-dedupe key so each case starts fresh.
export function resetPlacementSyncDivergenceDedupe(): void {
  lastEmittedDivergenceKey = null;
}

export const APP_TABS_BASE: AppTab[] = [
  {
    title: "Upload",
    mobileTitle: "Upload",
    mobileAccordionTitle: "Upload",
    value: "upload",
    canBeSkipped: () => false,
    icon: <CloudUpload className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isFileSelected }) => isFileSelected,
    isChecked: ({ isFileSelected }) => isFileSelected
  },
  {
    title: "Products",
    mobileTitle: "Products",
    mobileAccordionTitle: "Modify Products & Colors",
    value: "products",
    canBeSkipped: () => false,
    icon: <PaintBucket className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: () => true,
    isChecked: ({ visitedTabs }) => visitedTabs.has("products")
  },
  {
    title: "Select Sizes",
    mobileTitle: "Sizes",
    mobileAccordionTitle: "Edit Sizes",
    value: "size",
    canBeSkipped: () => false,
    icon: <Ruler className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: ({ isQuantityValid }) => isQuantityValid,
    isChecked: ({ isQuantityValid }) => isQuantityValid
  },
  {
    title: "Design",
    mobileTitle: "Design",
    mobileAccordionTitle: "Design",
    value: "design",
    canBeSkipped: () => false,
    icon: <Palette className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isFrontArtworkUploaded, isBackArtworkUploaded }) =>
      (visitedTabs.has("front") || visitedTabs.has("design")) &&
      (isFrontArtworkUploaded || isBackArtworkUploaded)
  }
];

const TAB_ORDER: Tab[] = ["upload", "design", "products", "size"];

function getOrderedTabs(): AppTab[] {
  const tabsMap = new Map(APP_TABS_BASE.map(tab => [tab.value, tab]));

  const ordered = TAB_ORDER.map(value => tabsMap.get(value)).filter((tab): tab is AppTab =>
    Boolean(tab)
  );
  return ordered.length === TAB_ORDER.length ? ordered : APP_TABS_BASE;
}

export function useAppTabs(): AppTab[] {
  return useMemo(() => getOrderedTabs(), []);
}

// Export APP_TABS for backward compatibility
// Components should use useAppTabs() hook instead
export const APP_TABS: AppTab[] = APP_TABS_BASE;

export function useTab() {
  const { lens, form: artworkForm } = useArtworkForm();
  const { isDesktop } = useResponsive();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);

  // Extract stable values to avoid re-renders
  const currentPlacementValue = currentPlacement.placement;
  const selectedFrontPlacementValue = selectedFrontPlacement.placement;
  const { setPlacement } = currentPlacement;

  const validation = useValidation();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};
  const setIsSizePlacementOpen = useSetAtom(mobileSizePlacementOpenAtom);
  const [activeSubTab] = useAtom(designSubTabAtom);
  const [designSubTabInitialized] = useAtom(designSubTabInitializedAtom);

  const [isInfoExpanded, setIsInfoExpanded] = useAtom(tabInfoExpandedAtom);
  const {
    tab,
    setTab: setTabNav,
    currentIndex,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  } = useTabNavigation({ artworkForm, ...(sides && { sides }) });

  const appTabs = useAppTabs();

  const tabs: AppTab[] = useMemo(() => {
    const validationWithSides = { ...validation, ...(sides && { sides }) };
    return appTabs.map(item => {
      let { title, mobileTitle, icon } = item;

      // Sample mode transformation
      if (isSample && item.value === "products") {
        title = "Colors";
        mobileTitle = "Colors";
      }

      if (item.value === "size") {
        title = "Sizes";
      }

      const ICON_META = {
        design: { normal: DesignIcon, selected: DesignSelectedIcon, label: "Design" },
        size: { normal: SizesIcon, selected: SizesSelectedIcon, label: "Sizes" },
        products: { normal: ProductsIcon, selected: ProductsSelectedIcon, label: "Products" }
      } as const;

      if (item.value in ICON_META) {
        const isSelected = item.value === tab;
        const color = isSelected ? "#155DFC" : "#171717";
        const meta = ICON_META[item.value as keyof typeof ICON_META];
        const IconComponent = isSelected ? meta.selected : meta.normal;

        icon = (
          <IconComponent
            className="size-6"
            style={{ fill: "none", stroke: color }}
            aria-label={meta.label}
          />
        );
      }

      return {
        ...item,
        title,
        mobileTitle,
        icon,
        valid: item.isValid?.(validationWithSides) ?? false,
        checked: item.isChecked?.(validationWithSides) ?? false,
        enabled: item.isEnabled?.(validationWithSides) ?? false
      };
    });
  }, [appTabs, isSample, validation, sides, tab]);

  const currentTabFromTabs = tabs.find(t => t.value === tab);
  const isValid = currentTabFromTabs?.valid ?? tabs[currentIndex]?.valid;

  const setTab = (value: Tab) => {
    setTabNav(value);

    let targetPlacement: "back" | typeof selectedFrontPlacement.placement | null = null;

    // Handle direct front/back tabs
    if (value === "front") {
      targetPlacement = selectedFrontPlacement.placement;
    } else if (value === "back") {
      targetPlacement = "back";
    }
    // Sync placement with activeSubTab for tabs with sub-tab UI
    // activeSubTab is global state tracking which sub-tab (front/back) is selected
    // Why: resolved via getPlacementFromSide (MN-9145) — the single
    // side->placement resolver shared with usePlacementNavigation, so a
    // corrupt selectedFrontPlacement of "back" is normalized instead of
    // leaking through as a front-side target.
    else if (value === "design" || value === "upload") {
      targetPlacement = getPlacementFromSide(activeSubTab, selectedFrontPlacement.placement);
    }

    if (targetPlacement) {
      currentPlacement.setPlacement(targetPlacement);
    }
  };

  // Only sync placement when tab or activeSubTab actually changes, not when placement changes
  const lastSyncedRef = useRef({ tab: "", activeSubTab: "" as DesignSubTab });

  // Tracks the previous value of designSubTabInitialized so the safety net can
  // recognise the single reconciliation triggered by the sanctioned init
  // fallback (which flips this atom false->true) and tag it apart from a
  // genuine unrouted writer. See the `reason` discriminator below.
  const wasInitializedRef = useRef(false);

  // SAFETY NET (MN-9145, kept deliberately — see MN-7799 rejection (b)):
  // every interactive placement transition is routed through
  // usePlacementNavigation, which commits designSubTabAtom and
  // currentPlacement in the same render, so for hook-routed writes this
  // effect is a provable no-op (currentPlacement already equals the target).
  // It is retained because designSubTabAtom is a public atom that is still
  // written directly by tests, hydration, and the sub-tab init fallback
  // (useDesignSubTabInitialization) — for those paths this post-commit
  // reconciliation is the only placement sync. When it actually corrects a
  // divergence it emits a breadcrumb tagged with a `reason`: "unrouted_writer"
  // (expected rate zero — the evidence gate for the effect's future removal)
  // vs "init_fallback" (the sanctioned init reconciliation, which carries an
  // expected non-zero baseline and must be filtered out before reading the
  // unrouted-writer rate). Do NOT remove without that evidence.
  useEffect(() => {
    // Check if tab or activeSubTab actually changed
    const hasChanged =
      lastSyncedRef.current.tab !== tab || lastSyncedRef.current.activeSubTab !== activeSubTab;

    if (!hasChanged) {
      return;
    }

    // Update ref to mark as processed
    lastSyncedRef.current = { tab, activeSubTab };

    // Sync placement with activeSubTab on design/upload tabs
    if (tab === "design" || tab === "upload") {
      const targetPlacement = getPlacementFromSide(activeSubTab, selectedFrontPlacementValue);

      if (currentPlacementValue !== targetPlacement) {
        // The sanctioned init fallback (useDesignSubTabInitialization) writes
        // the raw atom and flips designSubTabInitialized false->true in the
        // same commit, so the first reconciliation seen while that transition
        // is in flight is the init path, not a bug. Everything else is a
        // genuine unrouted writer.
        const reason =
          designSubTabInitialized && !wasInitializedRef.current
            ? "init_fallback"
            : "unrouted_writer";

        // MN-9145 T9/D9: dedupe the breadcrumb + dev warn across the ~10 mounted
        // useTab instances that all observe this same divergence WITHIN ONE
        // transition. Key on the full signature; concurrent instances compute an
        // identical key and collapse to a single emit. The key is cleared on the
        // microtask after the synchronous render batch (in which every concurrent
        // instance has already observed the transition), so the dedupe is scoped
        // to ONE transition — a temporally-separate LATER recurrence of the same
        // signature emits again instead of being suppressed for the whole SPA
        // session (which would under-count a recurring unrouted_writer regression
        // in the very metric that gates this effect's removal). The setPlacement
        // reconciliation stays outside the gate — it is idempotent and may run
        // per-instance.
        const emitKey = `${tab}|${activeSubTab}|${currentPlacementValue}|${targetPlacement}|${reason}`;
        if (emitKey !== lastEmittedDivergenceKey) {
          lastEmittedDivergenceKey = emitKey;
          queueMicrotask(() => {
            lastEmittedDivergenceKey = null;
          });

          // Why: an unrouted correction means some writer bypassed
          // usePlacementNavigation — surface it loudly in dev/test and leave a
          // production breadcrumb for the effect-removal follow-up. The
          // sanctioned init fallback is expected, so it is not warned about.
          // Bracket-access satisfies the TS index-signature rule (TS4111);
          // rspack's EnvironmentPlugin substitutes either access syntax.
          if (reason === "unrouted_writer" && process.env["NODE_ENV"] !== "production") {
            // eslint-disable-next-line no-console
            console.warn(
              "[design-lab] placement sync divergence: a designSubTabAtom write bypassed " +
                `usePlacementNavigation (tab=${tab}, activeSubTab=${activeSubTab}, ` +
                `currentPlacement=${currentPlacementValue} -> ${targetPlacement}). See MN-9145.`
            );
          }
          trackPlacementSyncDivergence({
            tab,
            activeSubTab,
            fromPlacement: currentPlacementValue,
            toPlacement: targetPlacement,
            reason
          });
        }
        setPlacement(targetPlacement);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeSubTab]);

  // Consume the designSubTabInitialized transition in a DEDICATED effect keyed
  // on [designSubTabInitialized] — NOT inside the safety-net effect above.
  // useDesignSubTabInitialization has paths that flip this atom false->true
  // WITHOUT changing activeSubTab (the "no special init" / project-loaded-no-
  // artwork / default branches), so the safety-net effect (deps
  // [tab, activeSubTab]) would never run to record that transition and the ref
  // would stay armed (false) — mis-tagging the next genuine unrouted writer as
  // init_fallback. This effect is declared AFTER the safety-net effect so that,
  // in a commit where init flips AND a reconciliation happens together (e.g.
  // ?side=back init), the safety-net effect still reads
  // wasInitializedRef.current === false first and tags that one reconciliation
  // init_fallback; this effect updates the ref to true immediately afterward.
  useEffect(() => {
    wasInitializedRef.current = designSubTabInitialized;
  }, [designSubTabInitialized]);

  const next = () => {
    setIsSizePlacementOpen(false);
    if (nextTabRaw) {
      setTab(nextTabRaw.value);
      trackTabNavigation(nextTabRaw.value, "next");
    }
  };

  const back = () => {
    setIsSizePlacementOpen(false);
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
      trackTabNavigation(prevTabRaw.value, "tabs");
    }
  };

  const toggleInfoExpanded = () => {
    setIsInfoExpanded(!isInfoExpanded);
  };

  useEffect(() => {
    if (isInfoExpanded === undefined) {
      setIsInfoExpanded(isDesktop);
    }
  }, [isDesktop, isInfoExpanded, setIsInfoExpanded]);

  return {
    tabs,
    tab,
    setTab,
    nextTab,
    prevTab,
    next,
    back,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isValid,
    isProductsTab,
    isSizeTab,
    isInfoExpanded,
    toggleInfoExpanded
  };
}
